import { apiGet, apiPost, apiPostFormData, apiPut, apiDelete } from "./api.ts";
import type {
  CategorySummary,
  CategoryDocument,
  Category,
  CategoryHierarchy,
  StandardCategoryItem,
  CategoryType,
} from "../types/Category";

export async function fetchCategories(categoryType: CategoryType) {
  return apiGet<{ data: CategorySummary[] }>(`/categories/${categoryType}`);
}

export async function fetchStandardCategories() {
  return apiGet<{ data: StandardCategoryItem[] }>(`/categories/standard/catalog`);
}

export async function fetchCategory(id: number, categoryType: string) {
  return apiGet<Category>(`/categories/${categoryType}/${id}`);
}

export async function fetchCategoryHierarchy(id: number, categoryType: string) {
  return apiGet<CategoryHierarchy | null>(
    `/categories/${categoryType}/${id}/hierarchy-nodes`
  );
}

export async function fetchCategoryDocuments(
  id: number,
  categoryType: string,
  nodeId: number,
  options?: { q?: string; scope?: "direct" | "subtree" }
) {
  const queryParams = new URLSearchParams();
  if (options?.q) {
    queryParams.append("q", options.q);
  }
  if (options?.scope) {
    queryParams.append("scope", options.scope);
  }
  const queryString = queryParams.toString();
  return apiGet<CategoryDocument[]>(
    `/categories/${categoryType}/${id}/hierarchy-nodes/${nodeId}/documents${queryString ? `?${queryString}` : ""}`
  );
}

export async function createCategory(input: {
  name: string;
  description?: string;
}) {
  return apiPost<
    {
      id: number;
      name: string;
      description?: string;
      type: "custom";
      createdAt: string;
      updatedAt: string;
    },
    typeof input
  >("/categories/custom", input);
}

export async function updateCustomCategory(
  categoryId: number,
  input: { name: string; description?: string }
) {
  return apiPut<
    {
      id: number;
      name: string;
      description?: string;
      type: "custom";
      conversationCount: number;
      pdfDocumentCount: number;
    },
    typeof input
  >(`/categories/custom/${categoryId}`, input);
}

export async function deleteCustomCategory(categoryId: number) {
  return apiDelete(`/categories/custom/${categoryId}`);
}

export async function uploadCategoryDocument(
  categoryId: number,
  categoryType: string,
  nodeId: number,
  file: File,
  name: string,
  description?: string
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", name);
  if (description) {
    formData.append("description", description);
  }
  return apiPostFormData<{ message: string }>(
    `/categories/${categoryType}/${categoryId}/hierarchy-nodes/${nodeId}/documents`,
    formData
  );
}

export async function fetchIngestionStatus(jobId: string) {
  return apiGet<{
    job_id: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    step?: number;
    step_name?: string;
    step_description?: string;
    steps_completed?: string[];
    total_steps?: number;
    filename?: string;
    error?: string | null;
    created_at?: string;
    completed_at?: string | null;
  }>(`/categories/ingestion-jobs/${jobId}`);
}

export interface IngestionJob {
  job_id: string;
  status: string;
  step?: number;
  step_name?: string;
  step_description?: string;
  steps_completed?: string[];
  total_steps?: number;
  filename?: string;
  error?: string | null;
  created_at?: string;
  completed_at?: string | null;
  category_id?: number;
  node_id?: number;
}

export async function fetchAllIngestionJobs(filters?: {
  nodeId?: number;
} | null) {
  if (filters === null) {
    return Promise.resolve([]);
  }
  
  const queryParams = new URLSearchParams();
  if (filters?.nodeId !== undefined && filters?.nodeId !== null) {
    queryParams.append('node_id', String(filters.nodeId));
  }
  const queryString = queryParams.toString();
  return apiGet<IngestionJob[]>(`/categories/ingestion-jobs${queryString ? `?${queryString}` : ''}`);
}

export async function addFolder(
  categoryId: number,
  input: {
    name: string;
    description?: string;
    parentNodeId?: number | null;
    categoryType?: string;
  }
) {
  const categoryType = input.categoryType ?? "custom";
  const body: {
    parentNodeId?: number | null;
    name: string;
    description?: string;
  } = {
    name: input.name,
  };

  if (input.parentNodeId !== undefined && input.parentNodeId !== null) {
    body.parentNodeId = input.parentNodeId;
  }
  if (input.description) {
    body.description = input.description;
  }

  return apiPost<
    {
      id: number;
      name: string;
      description?: string;
    },
    typeof body
  >(`/categories/${categoryType}/${categoryId}/hierarchy-nodes`, body);
}

export async function updateHierarchyNode(
  categoryId: number,
  nodeId: number,
  input: { name: string; description?: string },
  categoryType?: string
) {
  const type = categoryType ?? "custom";
  return apiPut<{ id: number; name: string; description?: string }, typeof input>(
    `/categories/${type}/${categoryId}/hierarchy-nodes/${nodeId}`,
    input
  );
}

export async function deleteHierarchyNode(
  categoryId: number,
  nodeId: number,
  categoryType?: string
) {
  const type = categoryType ?? "custom";
  return apiDelete(
    `/categories/${type}/${categoryId}/hierarchy-nodes/${nodeId}`
  );
}

export async function updateCategoryDocument(
  categoryId: number,
  nodeId: number,
  documentId: number,
  input: { name: string; description?: string }
) {
  return apiPut<{ id: number; name: string; description?: string | null }, typeof input>(
    `/categories/custom/${categoryId}/hierarchy-nodes/${nodeId}/documents/${documentId}`,
    input
  );
}

export async function deleteCategoryDocument(
  categoryId: number,
  nodeId: number,
  documentId: number
) {
  return apiDelete(
    `/categories/custom/${categoryId}/hierarchy-nodes/${nodeId}/documents/${documentId}`
  );
}